import { Modal } from 'antd'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Composable } from '../../utils/Composable'
import { NETWORK_ERROR_DEDUPE_KEY } from '../../utils/networkErrorConstants'
import { rumEvent } from '../../utils/rum'

export type UiErrorDetail = {
  title?: string
  content: string
  dedupeKey?: string
  dedupeTtlMs?: number
}

export interface ErrorInstance {
  destroy: () => void
  update: (errorDetailUpdate: UiErrorDetail) => void
}

export interface ErrorModalState {
  pushError: (error: UiErrorDetail) => void
  clearAllErrors: () => void
}

type DedupeEntry = {
  seenAt: number
  ttlMs: number
}

const DEFAULT_NETWORK_DEDUPE_TTL_MS = 30_000

export const ErrorModalContext = React.createContext<ErrorModalState>({
  pushError: _ => {},
  clearAllErrors: () => {},
})

const ErrorModal: React.FC<Composable> = ({ children }) => {
  const [pendingErrors, setPendingErrors] = useState<UiErrorDetail[]>([])
  const [, setIsModalOpen] = useState(false)
  const recentDedupeRef = useRef<Map<string, DedupeEntry>>(new Map())
  const activeModalRef = useRef<ReturnType<typeof Modal.error> | null>(null)

  const buildDedupeKey = useCallback((error: UiErrorDetail): string | undefined => {
    if (error.dedupeKey) return error.dedupeKey
    if (error.title === 'Network error') return NETWORK_ERROR_DEDUPE_KEY
    return undefined
  }, [])

  const pruneExpiredDedupeEntries = useCallback((now: number) => {
    const dedupeMap = recentDedupeRef.current
    for (const [key, entry] of dedupeMap.entries()) {
      if (now - entry.seenAt >= entry.ttlMs) {
        dedupeMap.delete(key)
      }
    }
  }, [])

  const shouldDedupe = useCallback(
    (dedupeKey: string, ttlMs: number): boolean => {
      const now = Date.now()
      pruneExpiredDedupeEntries(now)

      const existing = recentDedupeRef.current.get(dedupeKey)
      if (existing && now - existing.seenAt < existing.ttlMs) {
        return true
      }

      recentDedupeRef.current.set(dedupeKey, { seenAt: now, ttlMs })
      return false
    },
    [pruneExpiredDedupeEntries],
  )

  const clearAllErrors = useCallback(() => {
    Modal.destroyAll()
    activeModalRef.current = null
    setPendingErrors([])
    setIsModalOpen(false)
    recentDedupeRef.current.clear()
  }, [])

  const pushError = useCallback(
    (error: UiErrorDetail) => {
      const dedupeKey = buildDedupeKey(error)
      if (dedupeKey) {
        const ttlMs = error.dedupeTtlMs ?? DEFAULT_NETWORK_DEDUPE_TTL_MS
        if (shouldDedupe(dedupeKey, ttlMs)) {
          return
        }
      }

      setPendingErrors(prevErrors => [...prevErrors, error])
    },
    [buildDedupeKey, shouldDedupe],
  )

  useEffect(() => {
    if (pendingErrors.length === 0) return
    if (activeModalRef.current) return

    const [nextError] = pendingErrors
    setIsModalOpen(true)
    rumEvent('error_modal_shown', {
      title: nextError.title,
      dedupeKey: buildDedupeKey(nextError) ?? nextError.dedupeKey,
      contentPreview: nextError.content.slice(0, 120),
    })
    activeModalRef.current = Modal.error({
      ...nextError,
      afterClose: () => {
        activeModalRef.current = null
        setPendingErrors(prevErrors => prevErrors.slice(1))
        setIsModalOpen(false)
      },
    })
  }, [pendingErrors, buildDedupeKey])

  return <ErrorModalContext.Provider value={{ pushError, clearAllErrors }}>{children}</ErrorModalContext.Provider>
}

export const useErrorModal = () => React.useContext(ErrorModalContext)

export default ErrorModal
